import React from "react"

import { Form, Formik } from "formik"
import { NotificationManager } from "react-notifications"
import { useHistory } from "react-router-dom"
import { gql, useMutation } from "urql"
import * as Yup from "yup"

import { Color, Size } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import { Button } from "~tailwindui/Basics"
import {
  Failures,
  SubmitButton,
  TextAreaInput,
  handleFailure,
} from "~tailwindui/Form"
import Modal from "~tailwindui/Modal"
import { buttonClassNames } from "~tailwindui/helpers/classNameHelpers"

import { RunMutation } from "src/types"

const ValidationSchema = Yup.object().shape({
  cancellationReason: Yup.string().required(
    "Please provide a cancellation reason"
  ),
})

export type TutorCancelSessionButtonProps = {
  id: string
  size?: string
}

const TutorCancelSessionButton: React.FC<TutorCancelSessionButtonProps> = ({
  id,
  size,
}) => {
  const history = useHistory()

  const initialFormState = {
    cancellationReason: "",
  }

  const [, runMutation]: [any, RunMutation] = useMutation(cancelSessionMutation)

  const handleSubmit = (values, actions) => {
    actions.setStatus("Submitting...")
    runMutation({ id, cancellationReason: values.cancellationReason })
      .then(
        result => {
          const { failures } = result.data.cancelSession
          if (failures.length > 0) {
            handleFailure(actions, failures)
          } else {
            NotificationManager.success("The session has been canceled")
            history.push(path("dashboard"))
          }
        },
        () => handleFailure(actions)
      )
      .catch(() => handleFailure(actions))
  }

  return (
    <Modal.Dialog
      buttonText="Cancel"
      buttonClassNames={buttonClassNames({
        size: size === "small" ? Size.Small : Size.Medium,
        color: Color.Red,
      })}
    >
      {closeModal => (
        <>
          <Modal.Header>Cancel Session</Modal.Header>
          <Formik
            initialValues={initialFormState}
            onSubmit={(values, actions) => handleSubmit(values, actions)}
            validationSchema={ValidationSchema}
          >
            <Form className="flex max-h-[calc(70vh-90px)] flex-col">
              <Modal.Body>
                <p>
                  Are you sure you want to cancel this session? This action is
                  not reversible.
                </p>
                <TextAreaInput
                  label="Cancellation Reason"
                  name="cancellationReason"
                />
              </Modal.Body>
              <Modal.Footer>
                <Failures />
                <div className="flex justify-end space-x-2">
                  <Button color={Color.Blue} onClick={closeModal}>
                    No, I'll keep it
                  </Button>
                  <SubmitButton color={Color.Red} text="Yes, cancel it" />
                </div>
              </Modal.Footer>
            </Form>
          </Formik>
        </>
      )}
    </Modal.Dialog>
  )
}

const cancelSessionMutation = gql`
  mutation CancelSession($id: ID!, $cancellationReason: String!) {
    cancelSession(id: $id, cancellationReason: $cancellationReason) {
      failures {
        message
      }
    }
  }
`

export default TutorCancelSessionButton
